import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  Table,
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useSessionStore } from "../../stores/sessionStore";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../../stores/pagingStore";
import { api } from "../../config/middleware";
import {
  API_ADDBUSINESSTYPE,
  API_GETBUSINESSTYPE,
  API_EDITBUSINESSTYPE,
  API_DELBUSINESSTYPE,
} from "../../config/api";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import LoadingSpinner from "components/LoadingSpinner";

const TABLE_KEY = "businessType";

const AdminBusinessType = () => {
  const MySwal = withReactContent(Swal);

  const orgId = useSessionStore((s) => s.profile?.orgId);
  const { page, pageSize, setPage, setPageSize } = useTablePaging(TABLE_KEY);

  const [total, setTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [clientPaged, setClientPaged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [keyword, setKeyword] = useState("");
  const serverModeRef = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "" });
  const [submitting, setSubmitting] = useState(false);

  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggle = () => setIsOpen((v) => !v);
  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const reqIdRef = useRef(0);

  const getId = (it, i) =>
    it.cbId ?? it.BUSID ?? `${it.cbName ?? it.BUSNAME ?? "row"}-${i}`;

  const dedupeBy = (arr, idFn) => {
    const m = new Map();
    arr.forEach((x, i) => m.set(idFn(x, i), x));
    return [...m.values()];
  };

  const pickTotal = (d) =>
    Number(
      d?.total ?? d?.count ?? d?.pagination?.total ?? d?.meta?.total ?? NaN,
    );

  const norm = (s) => (s || "").toString().toLowerCase().trim();

  const baseRows = clientPaged ? allRows : rowsPage;
  const filteredRows = useMemo(() => {
    const k = norm(keyword);
    if (!k) return baseRows;
    return baseRows.filter((r) => norm(r.cbName ?? r.BUSNAME).includes(k));
  }, [baseRows, keyword]);

  const filteredTotal = filteredRows.length;
  const filteredPageCount = Math.max(1, Math.ceil(filteredTotal / pageSize));

  const displayRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const fetchFirstPage = async () => {
    if (!orgId) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    setClientPaged(false);
    serverModeRef.current = false;
    setRowsPage([]);
    setAllRows([]);
    setTotal(0);
    setPage(TABLE_KEY, 1);

    try {
      const res = await api.get(API_GETBUSINESSTYPE, {
        params: { ORGID: orgId, PAGE: 1, LIMIT: pageSize },
      });
      if (myReq !== reqIdRef.current) return;

      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const dedup = dedupeBy(raw, getId);
      const totalFromApi = pickTotal(res.data);
      const apiLooksPaged =
        Number.isFinite(totalFromApi) && dedup.length <= pageSize;

      if (apiLooksPaged) {
        setClientPaged(false);
        serverModeRef.current = true;
        setRowsPage(dedup);
        setTotal(totalFromApi);
      } else {
        serverModeRef.current = false;
        setClientPaged(true);
        const all = dedupeBy(raw, getId);
        setAllRows(all);
        setTotal(all.length);
      }
    } catch (e) {
      console.error("fetchFirstPage error:", e);
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  const fetchServerPage = async (nextPage) => {
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    try {
      const res = await api.get(API_GETBUSINESSTYPE, {
        params: { ORGID: orgId, PAGE: nextPage, LIMIT: pageSize },
      });
      if (myReq !== reqIdRef.current) return;
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const dedup = dedupeBy(raw, getId);
      setRowsPage(dedup);
    } catch (e) {
      console.error("fetchServerPage error:", e);
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  const fetchAllForSearch = async () => {
    if (!orgId) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    try {
      const first = await api.get(API_GETBUSINESSTYPE, {
        params: { ORGID: orgId, PAGE: 1, LIMIT: pageSize },
      });
      if (myReq !== reqIdRef.current) return;

      const firstRows = Array.isArray(first.data?.data) ? first.data.data : [];
      const totalAll = pickTotal(first.data) || firstRows.length;
      const pages = Math.max(1, Math.ceil(totalAll / pageSize));
      let all = dedupeBy(firstRows, getId);

      for (let p = 2; p <= pages; p++) {
        const r = await api.get(API_GETBUSINESSTYPE, {
          params: { ORGID: orgId, PAGE: p, LIMIT: pageSize },
        });
        if (myReq !== reqIdRef.current) return;
        const part = Array.isArray(r.data?.data) ? r.data.data : [];
        all = dedupeBy([...all, ...part], getId);
      }

      setClientPaged(true);
      serverModeRef.current = true;
      setAllRows(all);
      setTotal(all.length);
      setPage(TABLE_KEY, 1);
    } catch (e) {
      console.error("fetchAllForSearch error:", e);
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    if (!clientPaged) fetchServerPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!orgId) return;
    setKeyword("");
    fetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useEffect(() => {
    setPage(TABLE_KEY, 1);
    if (keyword && !clientPaged) fetchAllForSearch();
    if (!keyword && serverModeRef.current && clientPaged) {
      fetchFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const openAddModal = () => {
    setMode("add");
    setEditingId(null);
    setForm({ name: "" });
    setIsOpen(true);
  };

  const openEditModal = (row) => {
    setMode("edit");
    setEditingId(row.cbId ?? row.BUSID ?? null);
    setForm({ name: row.cbName ?? row.BUSNAME ?? "" });
    setIsOpen(true);
  };

  const addBusinessType = async () => {
    const payload = { BUSNAME: form.name.trim(), ORGID: orgId };
    const res = await api.post(API_ADDBUSINESSTYPE, payload);
    if ((res.data?.statustext || "").toUpperCase() !== "SUCCESS")
      throw new Error(res.data?.message || "เพิ่มไม่สำเร็จ");
  };

  const editBusinessType = async () => {
    const payload = {
      BUSID: editingId,
      BUSNAME: form.name.trim(),
      ORGID: orgId,
    };
    const res = await api.post(API_EDITBUSINESSTYPE, payload);
    if ((res.data?.statustext || "").toUpperCase() !== "SUCCESS")
      throw new Error(res.data?.message || "แก้ไขไม่สำเร็จ");
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "ยืนยันการลบ?",
      text: "คุณต้องการลบรายการนี้ใช่หรือไม่",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      const payload = {
        BUSID: row.cbId ?? row.BUSID,
        BUSSTATUS: "N",
        ORGID: orgId,
      };

      const res = await api.post(API_DELBUSINESSTYPE, payload);
      if ((res.data?.statustext || "").toUpperCase() !== "SUCCESS") {
        throw new Error(res.data?.message || "ลบไม่สำเร็จ");
      }

      await fetchFirstPage();

      Swal.fire({
        icon: "success",
        title: "ลบสำเร็จ",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: err.message || "ลบไม่สำเร็จ",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      await MySwal.fire({
        icon: "warning",
        title: "ข้อมูลไม่ครบ",
        text: "กรุณากรอกชื่อประเภทธุรกิจ",
      });
      return;
    }
    if (!orgId) {
      await MySwal.fire({
        icon: "error",
        title: "ไม่พบองค์กร",
        text: "ไม่พบ orgId",
      });
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "add") await addBusinessType();
      else await editBusinessType();

      await MySwal.fire({
        icon: "success",
        title: "บันทึกสำเร็จ",
        timer: 1300,
        showConfirmButton: false,
      });

      toggle();
      await fetchFirstPage();
    } catch (err) {
      await MySwal.fire({
        icon: "error",
        title: "บันทึกไม่สำเร็จ",
        text: err?.response?.data?.message || err.message || "เกิดข้อผิดพลาด",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const buildPages = (p, max) => {
    const pages = [];
    const start = Math.max(1, p - 1);
    const end = Math.min(max, p + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (start > 1) pages.unshift(1);
    if (end < max) pages.push(max);
    return [...new Set(pages)];
  };

  const pages = buildPages(page, filteredPageCount);

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            {/* ✅ Card ขาวแบบในรูป */}
            <Card className="zi-table-card">
              <CardHeader className="zi-table-head">
                <h6 className="mb-0">ประเภทธุรกิจ</h6>
                <Button color="success" onClick={openAddModal}>
                  เพิ่มประเภทธุรกิจ
                </Button>
              </CardHeader>

              <CardBody className="zi-table-body">
                <div className="mb-3">
                  <Row className="align-items-center g-0">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) =>
                          setPageSize(TABLE_KEY, Number(e.target.value))
                        }
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">แถว/หน้า</span>
                    </Col>

                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        type="text"
                        placeholder="ค้นหา ประเภทธุรกิจ"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="zi-search"
                      />
                    </Col>
                  </Row>
                </div>

                {/* ✅ Table โทนเดียวกับรูป */}
                <div className="zi-table-wrap">
                  {isSmall ? (
                    /* ✅ MOBILE: ใช้ table เดิมของคุณ */
                    <Table responsive hover className="table table-modern mb-0">
                      <thead className="thead-colored">
                        <tr>
                          <th className="text-start">ลำดับ</th>
                          <th className="text-start">ประเภทธุรกิจ</th>
                          <th
                            className="text-center"
                            style={{ width: "140px", paddingRight: "50px" }}
                          >
                            จัดการ
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayRows.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center text-muted">
                              ยังไม่มีข้อมูล
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((it, i) => (
                            <tr key={getId(it, i)}>
                              <td data-th="ลำดับ" className="text-start">
                                {(page - 1) * pageSize + (i + 1)}
                              </td>
                              <td data-th="ประเภทธุรกิจ">
                                {it.cbName ?? it.BUSNAME}
                              </td>
                              <td className="text-start" data-th="จัดการ">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => openEditModal(it)}
                                  >
                                    แก้ไข
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDelete(it)}
                                  >
                                    ลบ
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  ) : (
                    /* ✅ DESKTOP/TABLET: ใช้ UI แบบรูป */
                    <div className="zi-table-wrap">
                      <Table responsive hover className="zi-table mb-0">
                        <thead>
                          <tr>
                            <th className="text-start" style={{ width: 120 }}>
                              #
                            </th>
                            <th className="text-start" style={{ width: 420 }}>
                              ประเภทธุรกิจ
                            </th>
                            <th className="text-end" style={{ width: 50 }}>
                              การจัดการ
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {displayRows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={3}
                                className="text-center text-muted py-4"
                              >
                                ยังไม่มีข้อมูล
                              </td>
                            </tr>
                          ) : (
                            displayRows.map((it, i) => (
                              <tr key={getId(it, i)}>
                                <td className="text-start">
                                  {(page - 1) * pageSize + (i + 1)}
                                </td>
                                <td className="text-start fw-600">
                                  {it.cbName ?? it.BUSNAME}
                                </td>
                                <td className="text-end">
                                  <div className="d-inline-flex gap-2">
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => openEditModal(it)}
                                    >
                                      แก้ไข
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => handleDelete(it)}
                                    >
                                      ลบ
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-end mt-2">
                  <div className="text-muted small">
                    แสดง {(page - 1) * pageSize + (displayRows.length ? 1 : 0)}–
                    {Math.min(page * pageSize, filteredTotal)} จากทั้งหมด{" "}
                    {filteredTotal} รายการ
                  </div>
                </div>
              </CardBody>

              {filteredTotal > 0 && (
                <CardFooter
                  className="py-4"
                  style={{ position: "relative", zIndex: 10 }}
                >
                  <div className="paging-wrap">
                    <button
                      className="paging-btn icon"
                      onClick={() => setPage(TABLE_KEY, Math.max(1, page - 1))}
                      disabled={page === 1}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>

                    {pages.map((p) => (
                      <button
                        key={p}
                        className={`paging-btn ${page === p ? "active" : ""}`}
                        onClick={() => setPage(TABLE_KEY, p)}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(
                          TABLE_KEY,
                          Math.min(filteredPageCount, page + 1),
                        )
                      }
                      disabled={page === filteredPageCount}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={isOpen}
        toggle={toggle}
        centered
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={toggle}>
          {mode === "add" ? "เพิ่มประเภทธุรกิจ" : "แก้ไขประเภทธุรกิจ"}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <label className="form-control-label">ชื่อประเภทธุรกิจ</label>
              <Input
                name="name"
                value={form.name}
                onChange={onChange}
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggle}>
              ยกเลิก
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting
                ? mode === "edit"
                  ? "กำลังอัปเดต..."
                  : "กำลังบันทึก..."
                : mode === "edit"
                  ? "อัปเดต"
                  : "บันทึก"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* ✅ CSS แบบ Table ในรูป */}
      <style>{`
        .zi-table-card{
          background:#fff;
          border-radius:10px;
          border:1px solid rgba(15,23,42,.06);
          box-shadow:0 2px 10px rgba(15,23,42,.06);
          overflow:hidden;
        }
        .zi-table-head{
          background:#fff;
          border-bottom:1px solid rgba(15,23,42,.06);
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          padding:14px 16px;
        }
        .zi-table-body{
          padding:16px;
        }
        .zi-search{
          width:320px;
          min-width:320px;
          max-width:320px;
        }
        @media (max-width: 767.98px){
          .zi-search{ width:100%; min-width:0; max-width:100%; }
        }

        .zi-table-wrap{
          border-radius:10px;
          overflow:hidden;
          border:1px solid rgba(15,23,42,.06);
        }

        .zi-table{
          margin:0;
        }
        .zi-table thead th{
          background:#f7f9fc;
          color:#111827;
          font-weight:700;
          font-size:14px;
          padding:14px 16px;
          border-bottom:1px solid rgba(15,23,42,.06);
          white-space:nowrap;
        }
        .zi-table tbody td{
          padding:14px 16px;
          vertical-align:middle;
          border-top:1px solid rgba(15,23,42,.06);
          color:#111827;
          font-size:14px;
        }
        .zi-table tbody tr:hover td{
          background:#fbfcff;
        }
        .fw-600{ font-weight:600; }
      `}</style>
    </>
  );
};

export default AdminBusinessType;
